import React, { useCallback } from 'react';
import Icon from '../../components/Icon';
import useToast from '../../components/toast/useToast';
import './EmptyParty.css';

/**
 * EmptyParty — shown when a user lands in a private party with zero messages.
 *
 * Two CTAs:
 *   - Share invite → navigator.share() when available, otherwise falls through
 *     to clipboard copy (treated as a soft success — same toast).
 *   - Copy code    → navigator.clipboard.writeText(); toasts "Code copied".
 *
 * Hidden when partyCode === 'GLOB' or messages exist — that gate lives on the
 * caller (ChatRegion) so this component stays declarative.
 */
function EmptyParty({ code }) {
  const { showToast } = useToast();

  const inviteUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/?party=${encodeURIComponent(code)}`
      : `?party=${encodeURIComponent(code)}`;

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
        showToast('Code copied');
      } else {
        showToast('Copy unavailable');
      }
    } catch {
      showToast('Copy failed');
    }
  }, [code, showToast]);

  const handleShare = useCallback(async () => {
    const shareData = {
      title: 'Join my Glacier party',
      text: `Join my Glacier party · code ${code}`,
      url: inviteUrl,
    };
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch {
      // User-cancelled share counts as benign; fall through to clipboard.
    }
    // Fallback: copy the invite URL.
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(inviteUrl);
        showToast('Invite link copied');
        return;
      }
    } catch {
      /* no-op */
    }
    showToast('Sharing unavailable');
  }, [code, inviteUrl, showToast]);

  return (
    <section
      className="glacier-empty"
      aria-labelledby="glacier-empty-title"
      data-testid="empty-party"
    >
      <span className="glacier-empty__badge">
        <Icon name="users" size={12} aria-hidden />
        Empty room
      </span>
      <h3 id="glacier-empty-title" className="glacier-empty__title">
        It&apos;s lonely in here.
      </h3>
      <p className="glacier-empty__desc">
        Share the code so your friends can drop in. Parties auto-close after
        24h of silence.
      </p>
      <div className="glacier-empty__code" aria-label={`Party code ${code}`}>
        <Icon name="lock" size={14} aria-hidden />
        <span>{code}</span>
      </div>
      <div className="glacier-empty__actions">
        <button
          type="button"
          className="glacier-empty__btn glacier-empty__btn--primary"
          onClick={handleShare}
        >
          <Icon name="share" size={14} aria-hidden />
          Share invite
        </button>
        <button
          type="button"
          className="glacier-empty__btn glacier-empty__btn--ghost"
          onClick={handleCopy}
        >
          <Icon name="copy" size={14} aria-hidden />
          Copy
        </button>
      </div>
    </section>
  );
}

export default EmptyParty;
